import { useCustomerContext } from '@/context/CustomerContext'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import { useModalContext } from '@/context/ModalContext'
import { formatPrice } from '@/utils/formatPrice'
import { useMemo } from 'react'
import classes from './ProductPricing.module.scss'

function ProductPricing({
  product,
  selectedVariant,
  mainPriceOverride,
  compareAtPriceOverride,
}) {
  const modalContext = useModalContext()
  const { getEligibleDiscounts } = useHeadlessCheckoutContext()
  const { customer } = useCustomerContext()

  const openLoginModal = (e) => {
    e.preventDefault()
    modalContext.setIsOpen(false)
    // console.log(modalContext.isOpen)
    modalContext.setModalType('login-noredirect')
    modalContext.setIsOpen(true)
  }

  const eligibleDiscounts = useMemo(
    () => getEligibleDiscounts({ lineItems: [{ ...product }] }),
    [customer, product],
  )

  const priceWrap = useMemo(() => {
    const isGiftCard = product.content?.handle === 'digital-gift-card'
    const isSub = product.tags?.includes('Subscription Box')
    const isAddOn = product.tags?.includes('Add-On')
    const isSwap = product.tags?.includes('Swap')
    const isMealKit = product.tags?.includes('Meal Kit')

    const displayDiscounts =
      !isGiftCard &&
      !isSub &&
      !isAddOn &&
      !isSwap &&
      !mainPriceOverride &&
      !isMealKit

    const hasApplicableDiscount = !!eligibleDiscounts[0]

    const comparePriceDisplay = (selectedVariant?.compareAtPrice ||
      compareAtPriceOverride) && (
      <p
        data-testid="compare-price"
        className={`${classes['secondary-price']} ${classes['strikethrough-price']}`}
      >
        $
        {formatPrice(
          (compareAtPriceOverride
            ? compareAtPriceOverride
            : selectedVariant?.compareAtPrice) * 100,
        )}
      </p>
    )

    const mainPriceDisplay = (
      <p
        data-testid="main-price"
        className={`${hasApplicableDiscount && classes.compare} ${hasApplicableDiscount ? classes['secondary-price'] : classes['primary-price']} ${hasApplicableDiscount && classes['strikethrough-price']}`}
      >
        $
        {formatPrice(
          (mainPriceOverride ? mainPriceOverride : selectedVariant.price) * 100,
        )}
      </p>
    )

    const discountDisplay = hasApplicableDiscount ? (
      <p
        data-testid="applicable-discount-price"
        className={`${classes['primary-price']}`}
      >
        <b>
          $
          {formatPrice(
            eligibleDiscounts[0].getDiscountedPrice(selectedVariant.price) *
              100,
          )}
        </b>{' '}
        with {eligibleDiscounts[0].name}
      </p>
    ) : (
      <p
        data-testid="discount-price"
        className={`${classes['secondary-price']}`}
      >
        <a onClick={openLoginModal}>
          Log in for <b>${formatPrice(selectedVariant.price * 90)}</b> with 10%
          Member Discount
        </a>
      </p>
    )

    const loggedInDiscountDisplay = hasApplicableDiscount ? (
      <p
        data-testid="applicable-discount-price"
        className={`${classes['primary-price']}`}
      >
        <b>
          $
          {formatPrice(
            eligibleDiscounts[0].getDiscountedPrice(selectedVariant.price) *
              100,
          )}
        </b>{' '}
        with {eligibleDiscounts[0].name}
      </p>
    ) : (
      <p
        data-testid="no-applicable-discount-price"
        className={`${classes['secondary-price']}`}
      >
        ${formatPrice(selectedVariant.price * 90)} with 10% Member Discount
      </p>
    )

    return (
      <div className={classes['price']}>
        {comparePriceDisplay}
        {mainPriceDisplay}
        {displayDiscounts &&
          (customer ? loggedInDiscountDisplay : discountDisplay)}
      </div>
    )
  }, [product, selectedVariant, customer, mainPriceOverride])

  return (
    product && (
      <div className={classes['price-wrap']}>
        {priceWrap}
        {!!selectedVariant.weight && (
          <p className={`${classes['weight']}`}>{selectedVariant.weight}lbs</p>
        )}
      </div>
    )
  )
}

export default ProductPricing
